import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Plus, X, Check } from 'lucide-react';
import { getMyBudgetAlerts, setMyBudgetAlerts } from '../api';

const PRESETS = [50, 75, 90, 100];

function BudgetCard({ label, spent, budget, pct }) {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-4">
      <div className="text-sm text-fg mb-1.5">{label}</div>
      <div className="text-sm font-semibold text-fg mb-2">
        ${spent.toFixed(2)} <span className="text-fg-muted font-normal">/ ${budget.toFixed(2)}</span>
        <span className="text-fg-muted font-normal"> ({pct}%)</span>
      </div>
      <div className="w-full h-2 bg-field rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            pct >= 100 ? 'bg-red-500' : pct >= 90 ? 'bg-orange' : 'bg-emerald-500'}`}
          style={{ width: `${Math.min(100, pct || 0)}%` }}
        />
      </div>
    </div>
  );
}

const EMPTY_BUDGET_STATUS = { dailyBudget: 0, spentToday: 0, currentPct: null, teams: [] };

export default function Alerts() {
  const [enabled, setEnabled] = useState(false);
  const [thresholds, setThresholds] = useState([]);
  const [custom, setCustom] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // Always set/read together from one API response — one state, not four.
  const [budgetStatus, setBudgetStatus] = useState(EMPTY_BUDGET_STATUS);
  const { dailyBudget, spentToday, currentPct, teams } = budgetStatus;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMyBudgetAlerts();
      setEnabled(!!r.data.enabled);
      setThresholds(r.data.thresholds || []);
      setBudgetStatus({
        dailyBudget: r.data.daily_budget_usd || 0,
        spentToday: r.data.spent_today_usd || 0,
        currentPct: r.data.current_pct,
        teams: r.data.teams || [],
      });
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addThreshold = (v) => {
    const n = parseInt(v, 10);
    if (!n || n < 1 || n > 100 || thresholds.includes(n)) return;
    setThresholds((t) => [...t, n].sort((a, b) => a - b));
  };
  const removeThreshold = (n) => setThresholds((t) => t.filter((x) => x !== n));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await setMyBudgetAlerts(enabled, thresholds);
      setThresholds(r.data.thresholds || []);
      setMsg({ kind: 'ok', text: 'Alert settings saved.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Failed to save' });
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-center text-fg-muted py-16">Loading…</div>;

  // No context (personal or any team) has a daily cap at all — evaluate_and_alert
  // always no-ops without one, so configuring thresholds here would silently do
  // nothing. Disable rather than let someone save a setting that can never fire.
  const hasAnyBudget = dailyBudget > 0 || teams.length > 0;

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
          <Bell className="w-6 h-6 text-orange" /> Budget Alerts
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          Get an email when your <span className="text-fg">daily</span> spend crosses a percentage of your budget.
          You're alerted once per threshold per day.
        </p>
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg border text-sm ${
          msg.kind === 'ok' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
                            : 'bg-red-500/10 border-red-500/25 text-red-400'}`}>
          {msg.text}
        </div>
      )}

      {/* Where you actually stand today — so thresholds below aren't picked blind.
          One card for your own (non-team) usage, plus one per team you're in —
          each team can carry a different tier/cap, so there's no single
          combined number that could represent "team" usage. */}
      {hasAnyBudget ? (
        <div className="mb-4">
          <h2 className="font-semibold text-fg mb-2">Today's spend</h2>
          <div className="space-y-3">
            {dailyBudget > 0 && <BudgetCard label="Personal" spent={spentToday} budget={dailyBudget} pct={currentPct} />}
            {teams.map((tm) => (
              <BudgetCard key={tm.project_id} label={`Team: ${tm.name || tm.project_id}`}
                spent={tm.spent_today_usd} budget={tm.daily_budget_usd} pct={tm.current_pct} />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-panel border border-panel-border rounded-xl p-5 mb-4 text-sm text-fg-muted">
          No daily budget limit is set for your account or any team you belong to, so there's nothing to alert on yet.
        </div>
      )}

      <div className={`bg-panel border border-panel-border rounded-xl p-6 space-y-6 ${hasAnyBudget ? '' : 'opacity-50 pointer-events-none'}`}>
        {/* master toggle */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-fg font-semibold">Email alerts {enabled ? 'on' : 'off'}</div>
            <div className="text-fg-muted text-xs">
              {hasAnyBudget ? 'When off, no alert emails are sent.' : 'No daily budget limit is set, so there is nothing to alert on.'}
            </div>
          </div>
          <button onClick={() => setEnabled((e) => !e)} disabled={!hasAnyBudget}
            className={`relative w-14 h-7 rounded-full transition-colors ${enabled ? 'bg-orange' : 'bg-field border border-field-border'}`}>
            <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all ${enabled ? 'left-7' : 'left-0.5'}`} />
          </button>
        </div>

        {/* thresholds */}
        <div className={enabled ? '' : 'opacity-50 pointer-events-none'}>
          <label className="block text-sm text-fg-muted mb-2">Alert me at these % of my daily budget</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {thresholds.length === 0 && <span className="text-fg-muted text-sm">No thresholds yet — add one below.</span>}
            {thresholds.map((n) => (
              <span key={n} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange/15 text-orange text-sm">
                {n}%
                <button onClick={() => removeThreshold(n)} className="hover:text-fg"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.filter((p) => !thresholds.includes(p)).map((p) => (
              <button key={p} onClick={() => addThreshold(p)}
                className="px-2.5 py-1 rounded-lg bg-field border border-field-border text-fg-muted hover:text-fg hover:border-orange text-xs">
                + {p}%
              </button>
            ))}
            <div className="inline-flex items-center gap-1">
              <input type="number" min="1" max="100" value={custom} onChange={(e) => setCustom(e.target.value)}
                placeholder="custom"
                className="w-20 px-2 py-1 bg-field border border-field-border rounded text-fg text-xs" />
              <button onClick={() => { addThreshold(custom); setCustom(''); }}
                className="p-1.5 bg-field border border-field-border hover:border-orange text-fg-muted hover:text-fg rounded">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <button onClick={save} disabled={busy || !hasAnyBudget}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm font-semibold disabled:opacity-50">
          <Check className="w-4 h-4" /> {busy ? 'Saving…' : 'Save alerts'}
        </button>
      </div>

      <p className="text-xs text-fg-muted mt-3">
        Tip: 100% means you've exhausted the budget — further requests are blocked with HTTP 402 until the window resets.
      </p>
    </div>
  );
}
